import { forwardRef } from 'react';
import styled from 'styled-components';

import createCustomParser from '../createCustomParser';
import type { GridItemProps } from '../extensions';
import { gridItem } from '../extensions';
import styledFactory from '../styledFactory';

const systemProp = (name: string, property: string, scale: string) =>
  createCustomParser({
    [name]: {
      property,
      scale,
    },
  });

const betweenVertical = systemProp('vBetween', 'marginTop', 'space');
const betweenHorizontal = systemProp('hBetween', 'marginLeft', 'space');

export interface Props extends GridItemProps {
  between?: any; //ResponsiveValue<ThemeValue<'space', DefaultTheme>>;
  axis?: 'horizontal' | 'vertical';
}

const SystemStack = styledFactory<Props, 'div'>('div', 'Stack', [gridItem]);

const StyledStack = styled(SystemStack)<
  Props & { vBetween: Props['between']; hBetween: Props['between'] }
>`
  > * + :not([data-no-between='true']) {
    ${betweenVertical}
    ${betweenHorizontal}
  }
`;

/**
 * A box for spacing children evenly between each other.
 * The spacing between prop work just as https://styled-system.com/#space-theming
 *
 * Add `data-no-between="true"` to a child element to override the stacking
 * behaviour on that element.
 *
 * See also Rhythm for spacing children relative to their font size.
 */
const Stack = forwardRef<
  HTMLDivElement,
  Omit<React.ComponentPropsWithRef<typeof StyledStack>, 'vBetween' | 'hBetween'>
>(({ axis = 'vertical', between, children, ...props }, ref) => (
  <StyledStack
    ref={ref}
    vBetween={axis === 'vertical' ? between : undefined}
    hBetween={axis === 'horizontal' ? between : undefined}
    display={axis === 'horizontal' ? 'flex' : undefined}
    {...props}
  >
    {children}
  </StyledStack>
));

export default Stack;
